<template>
<div class="bc-note-composer">
    <el-form
      ref="noteForm"
      :model="note"
      @submit.native.prevent >
      <el-form-item
        label="Title"
        prop="title"
        scope="noteForm"
      >
          <el-input
            name="title"
            autofocus
            v-model="note.title"
            v-validate="'required'"
            @input="$errors.remove('title')"
            placeholder="Title"
            data-element="title"
            data-vv-validate-on="input"
            data-vv-scope="noteForm"
          />
      </el-form-item>
      <el-form-item
        label="Content"
        prop="body"
        scope="noteForm"
      >
        <note-editor
          name="body"
          v-model="note.body"
          v-validate="'required'"
          @input="$errors.remove('body')"
          data-vv-validate-on="input"
          data-vv-scope="noteForm"
        />
      </el-form-item>
    </el-form>
    <hr class="u-m0">
    <notes-markdown-help-modal class="u-mb3"/>
    <div
      class="bc-note-composer__form"
    >
      <el-form
        ref="noteForm"
        :model="note"
        @submit.native.prevent
      >
        <el-form-item
          label="Note type"
          prop="noteType"
          scope="noteForm"
        >
          <el-select
            v-model="note.note_type"
            filterable
            default-first-option
            placeholder="Choose note type"
            name="noteType"
            v-validate="'required'"
            data-vv-validate-on="change|blur"
            data-vv-scope="noteForm"
          >
            <el-option
              v-for="item in noteTypeOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              :disabled="!item.active"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item
          label="Tags"
          prop="tags">
          <el-select
            v-model="note.tags"
            multiple
            :multiple-limit=10
            filterable
            clearable
            default-first-option
            placeholder="Choose tags"
          >
            <el-option
              v-for="item in noteTagOptions"
              :key="item.id"
              :label="item.name"
              :value="item.id"
              :disabled="!item.active"
            >
            </el-option>
          </el-select>
        </el-form-item>
        <slot
          name="creation-form"
          :form="note"
        />
      </el-form>
    </div>
    <div
      class="l-flex-grid l-flex-grid--v-center">
      <el-button
        data-test="cancel-button"
        :disabled="isFetching"
        @click="onCancel"
        class="u-ml-auto"
        size="medium" >
        Cancel
      </el-button>
      <el-button
        data-test="save-button"
        :disabled="isFetching"
        @click="onSave"
        size="medium"
        type="primary" >
        Save
      </el-button>
    </div>
  </div>
</template>

<style lang="scss" src="./_note-composer.scss"></style>

<script>
import {mapState, mapGetters, mapMutations} from 'vuex'
import NoteEditor from '#/notes/components/NoteEditor'
import NotesMarkdownHelpModal from '#/notes/components/NotesMarkdownHelpModal'
import {cloneDeep} from 'lodash'
import notesMutations from '#/notes/store/mutationTypes'

export default {
  name: 'NoteComposer',
  inject: ['$validator'],
  components: {
    NoteEditor,
    NotesMarkdownHelpModal
  },
  async created () {
    this.user = await this.userService.fetchLoggedInUserAttributes()
  },
  data () {
    return {
      note: {
        title: '',
        body: '',
        note_type: ''
      },
      user: {},
      currentNoteTitle: '',
      currentNoteBody: '',
      currentNoteType: ''
    }
  },
  props: {
    customDataFunction: {
      type: Function,
      default: () => ({})
    },
    customCreationForm: {
      type: Object,
      default: () => ({})
    },
    reference: {
      type: Object,
      default: null
    },
    noteData: {
      type: Object,
      default: null
    }
  },
  watch: {
    note: {
      handler (note) {
        if (note.title !== this.currentNoteTitle || note.body !== this.currentNoteBody || note.note_type !== this.currentNoteType) {
          this.noteIsDirty(true)
        } else {
          this.noteIsDirty(false)
        }
      },
      immediate: true,
      deep: true
    },
    customCreationForm: {
      handler (form) {
        this.note = {
          ...this.note,
          ...form
        }
      },
      immediate: true
    },
    noteData: {
      async handler (noteData) {
        if (!noteData) return
        this.note = this.deserializeNoteRelationships({
          ...noteData,
          ...this.customCreationForm
        })
        this.updateOptions(noteData)
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('notes', [
      'isFetching',
      'noteTypes',
      'noteTags'
    ]),
    ...mapGetters('notes', [
      'userService'
    ]),
    isNewNote () {
      return !this.note.id
    },
    noteTypeOptions: {
      get () {
        return cloneDeep(this.noteTypes)
      },
      set (value) {
        this.setNoteTypes(value)
      }
    },
    noteTagOptions: {
      get () {
        return cloneDeep(this.noteTags)
      },
      set (value) {
        this.setNoteTags(value)
      }
    }
  },
  destroyed () {
    this.noteIsDirty(false)
  },
  mounted () {
    this.currentNoteTitle = this.note.title
    this.currentNoteBody = this.note.body
    this.currentNoteType = this.note.note_type
    this.noteIsDirty(false)
  },
  methods: {
    ...mapMutations({
      noteIsDirty: notesMutations.SET_NOTE_IS_DIRTY,
      setNoteTypes: notesMutations.SET_NOTE_TYPES,
      setNoteTags: notesMutations.SET_NOTE_TAGS
    }),
    onCancel () {
      this.$emit('cancel')
    },
    async onSave () {
      const isValid = await this.validateNote()
      if (!isValid) return

      const note = this.createNoteStructure(cloneDeep(this.note))
      this.$emit('save-note-click', {...note})
    },
    async validateNote () {
      return await this.$validator.validate('noteForm.*')
    },
    deserializeNoteRelationships (note) {
      note.note_type = note.note_type ? note.note_type.id : null
      note.tags = note.tags.map(tag => tag.id)
      return note
    },
    updateOptions (note) {
      // ensure options are updated post DOM update.
      this.$nextTick(() => {
        // include inactive note type
        if (note.note_type && !note.note_type.active) {
          this.noteTypeOptions = [...this.noteTypes, cloneDeep(note.note_type)]
        }
        // include inactive note tags
        const tags = []
        note.tags.map(tag => {
          if (!tag.active) {
            tags.push(tag)
          }
        })
        this.noteTagOptions = [...this.noteTags, ...cloneDeep(tags)]
      })
    },
    serializeNoteRelationships (note) {
      if (note.note_type) {
        note.note_type = {id: note.note_type}
      } else {
        delete note.note_type
      }
      note.tags = note.tags.map(tag => ({id: tag}))
      return note
    },
    createNoteStructure (note) {
      note = this.serializeNoteRelationships(note)
      note.reference = this.reference
      note.author_name = this.user.name
      if (!this.isNewNote) {
        delete note.creator
      }
      note.custom_data = this.customDataFunction({noteForm: note})
      return note
    }
  }
}

</script>
